import { Eye } from "iconsax-reactjs"
import { EyeClosed } from "lucide-react"
import { useMemo } from "react"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"

import { HiddenBalance } from "@/features/earns/components/summary/balance"
import { useShowBalance } from "@/modules/helpers/hooks/shared"

type BankChartProps = {
  data?: {
    balance: number
    earnPending: number
    withdrawn: number
    withdrawalProgressing: number
    earned: number
  }
}

export function ReportChart({ data }: BankChartProps) {
  const { showTotalEarn, setShowTotalEarn } = useShowBalance()
  const [total, pieData] = useMemo(() => {
    const total =
      (data?.balance ?? 0) +
      (data?.earnPending ?? 0) +
      (data?.withdrawn ?? 0) +
      (data?.withdrawalProgressing ?? 0)
    return [
      total,
      total
        ? [
            { name: "Số dư khả dụng", value: data?.balance ?? 0, color: "#045664" },
            { name: "Đã rút", value: data?.withdrawn ?? 0, color: "#0091AA" },
            { name: "Dự kiến nhận", value: data?.earnPending ?? 0, color: "#63C3D4" },
            { name: "Đã yêu cầu rút", value: data?.withdrawalProgressing ?? 0, color: "#C2E1E6" }
          ]
        : [{ name: "Chưa có dữ liệu", value: 100, color: "#e9f3f6" }]
    ]
  }, [data])

  return (
    <div className="bg-white mt-4 ">
      <div className="flex flex-col md:flex-row items-center gap-6 mt-4">
        {/* Pie Chart */}
        <div className="w-full h-62 relative">
          <ResponsiveContainer className="focus-within:!outline- !border-0">
            <PieChart className="focus-within:!outline-none">
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                className="focus-within:!outline-none"
                innerRadius={70}
                outerRadius={100}
                paddingAngle={0}
                dataKey="value"
                isAnimationActive={false}
              >
                {!Number(total) && (
                  <Cell
                    key={`cell-0`}
                    fill={pieData[0].color}
                    className="focus-within:outline-none"
                  />
                )}
                {!!total &&
                  pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      className="focus-within:outline-none"
                    />
                  ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {!!total && (
            <div className="absolute inset-0 z-50 flex flex-col justify-center items-center">
              <div className="flex items-center gap-2 mt-2 justify-center">
                <span className="text-sm font-light">Tổng thu nhập</span>
                <div
                  onClick={() => setShowTotalEarn(!showTotalEarn)}
                  className="
                cursor-pointer h-4 w-4 rounded-full flex items-center justify-center"
                >
                  {showTotalEarn ? (
                    <Eye size={16} color="#0091AA" />
                  ) : (
                    <EyeClosed size={16} color="#0091AA" />
                  )}
                </div>
              </div>{" "}
              <div className="flex items-center gap-2">
                {showTotalEarn ? (
                  <p className="text-sm font-semibold text-gradient-primary">
                    {Number(data?.earned).toLocaleString()} ₫
                  </p>
                ) : (
                  <HiddenBalance />
                )}
              </div>
            </div>
          )}
          {!total && (
            <div className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none">
              <span className="text-xs font-medium text-primary-800">Chưa có dữ liệu</span>
            </div>
          )}
        </div>

        {/* Labels */}
        <div className="flex-1 grid grid-cols-2 w-full px-4 gap-4">
          {!!Number(total) &&
            pieData?.map((item, i) => (
              <div key={i} className="flex flex-col flex-1">
                <div className="flex items-start gap-2">
                  <div className="w-1 h-10 mt-0.5" style={{ backgroundColor: item.color }}></div>
                  <div className="flex flex-col gap-1 flex-1 whitespace-nowrap">
                    <p className="text-xs whitespace-nowrap font-light">{item.name}</p>
                    <p className="text-sm font-semibold whitespace-nowrap text-gradient-primary">
                      {Number(item.value).toLocaleString()} ₫
                    </p>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
